import axios from "axios";
import { SURVEY_API_ENDPOINT } from "../constants";
import Url from "app/shared/constants/Url";

class SurveyApiService {
  // Read dynamically from localStorage to avoid stale token states
  get userId() {
    return localStorage.getItem("userId");
  }

  get token() {
    return localStorage.getItem("token");
  }

  get springToken() {
    return localStorage.getItem("springToken");
  }

  // Create standard FormData helper for POST requests
  createFormData(data = {}) {
    const formData = new FormData();
    formData.append("auth_key", this.springToken || this.token);
    formData.append("user_id", this.userId);
    formData.append("form_type", 8);

    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => formData.append(key, item));
      } else if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });

    return formData;
  }

  // Fetch client counts/dashboard statistics
  async fetchClientCounts() {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };

      const response = await axios.get(SURVEY_API_ENDPOINT.counts, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching client counts:", error);
      throw error;
    }
  }

  // Fetch survey stats — used for the stat cards on the Client Master page
  async fetchSurveyStats() {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };

      const response = await axios.get(SURVEY_API_ENDPOINT.surveyStats, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching survey stats:", error);
      throw error;
    }
  }

  // Fetch list of survey categories — GET /survey/categories
  async fetchCategories(params = {}) {
    try {
      const queryParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...params,
      };

      const response = await axios.get(SURVEY_API_ENDPOINT.categories, { params: queryParams });
      return response.data;
    } catch (error) {
      console.error("Error fetching survey categories:", error);
      throw error;
    }
  }

  // Fetch list of clients — GET against the /survey nodeUrl endpoint
  async fetchClients(params = {}) {
    try {
      const { tab, search, ...rest } = params;
      const requestParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        is_deleted: tab === "active" ? 0 : 1,
        ...rest,
      };

      const response = await axios.get(SURVEY_API_ENDPOINT.listNode, {
        params: requestParams,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching clients:", error);
      throw error;
    }
  }

  // Fetch a single client's details — GET /client/:id. Used to prefill the
  // create/view client form when opening an existing client's record.
  async fetchClientDetails(clientId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };

      const response = await axios.get(SURVEY_API_ENDPOINT.details(clientId), { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching client details:", error);
      throw error;
    }
  }

  // Export a client — GET /client/:id/export. Returns the raw file as a blob
  // (plus response headers) so the caller can trigger a browser download.
  async exportClient(clientId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };

      const response = await axios.get(SURVEY_API_ENDPOINT.export(clientId), {
        params,
        responseType: "blob",
      });
      return response;
    } catch (error) {
      console.error("Error exporting client:", error);
      throw error;
    }
  }

  // Upload a client logo — POST /client/logo (multipart/form-data: logo, userId)
  async uploadLogo(logoFile) {
    try {
      const formData = new FormData();
      formData.append("logo", logoFile);
      formData.append("userId", this.userId);

      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };

      const response = await axios.post(SURVEY_API_ENDPOINT.logoUpload, formData, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      console.error("Error uploading logo:", error);
      throw error;
    }
  }

  // Build the JSON body for POST /client — maps the camelCase form state to
  // the snake_case client schema. logoId is the id returned by uploadLogo().
  buildClientPayload(clientData = {}, logoId) {
    const body = {
      client_name: clientData.clientName,
      short_name: clientData.shortName || clientData.short_name,
      shortName: clientData.shortName || clientData.short_name,
      business_sector: clientData.businessSector || clientData.business_sector,
      businessSector: clientData.businessSector || clientData.business_sector,
      description: clientData.description,
      industry_type: clientData.industryType,
      company_registration_number: clientData.companyRegNumber,
      gst_number: clientData.gstNumber,
      pan_number: clientData.panNumber,
      website: clientData.website,
      linkedin_profile: clientData.linkedinProfile,
      company_size: clientData.companySize,
      building_number: clientData.buildingNumber,
      addition_number: clientData.additionNo,
      street: clientData.street,
      country_id: clientData.countryId,
      country: clientData.country,
      state: clientData.state,
      city: clientData.city,
      district: clientData.district,
      zip_code: clientData.zipCode,
      po_box: clientData.poBox,
    };

    if (logoId !== undefined && logoId !== null) {
      body.logo = { id: logoId };
    }

    // Drop undefined/null so we don't send empty fields the backend didn't ask for
    Object.keys(body).forEach((key) => {
      if (body[key] === undefined || body[key] === null || body[key] === "") delete body[key];
    });

    return body;
  }

  // Create a new client — POST /client (application/json), auth as query params
  async createClient(clientData, logoId) {
    try {
      const body = this.buildClientPayload(clientData, logoId);
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.post(SURVEY_API_ENDPOINT.create, body, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error creating client:", error);
      throw error;
    }
  }

  // Update an existing client — PUT /client/{id} (application/json), auth as query params
  async updateClient(clientId, clientData, logoId) {
    try {
      const body = this.buildClientPayload(clientData, logoId);
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.put(SURVEY_API_ENDPOINT.update(clientId), body, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error updating client:", error);
      throw error;
    }
  }

  // Delete an existing client — DELETE /client/{id} or DELETE /survey/{id}
  async deleteClient(clientId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };

      let response;
      try {
        response = await axios.delete(SURVEY_API_ENDPOINT.clientDelete(clientId), { params });
      } catch (err) {
        response = await axios.delete(SURVEY_API_ENDPOINT.delete(clientId), { params });
      }

      let resData = response?.data || response || {};
      let msg = String(resData?.message || resData?.msg || "").toLowerCase();

      // If backend only deleted survey template ("Survey form template deleted successfully"),
      // execute second call immediately so the client record is deleted in 1 click!
      if (msg.includes("template") || msg.includes("survey form") || msg.includes("form template")) {
        try {
          const secondRes = await axios.delete(SURVEY_API_ENDPOINT.delete(clientId), { params });
          resData = secondRes?.data || resData;
        } catch (e) {
          try {
            const secondRes = await axios.delete(SURVEY_API_ENDPOINT.clientDelete(clientId), { params });
            resData = secondRes?.data || resData;
          } catch (e2) {}
        }
      }

      return resData;
    } catch (error) {
      console.error("Error deleting client:", error);
      throw error;
    }
  }

  // Restore a previously deleted/archived client — PUT /survey/{id}/retrieve
  async restoreClient(clientId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };

      const response = await axios.put(SURVEY_API_ENDPOINT.restore(clientId), null, { params });
      return response.data;
    } catch (error) {
      console.error("Error restoring client:", error);
      throw error;
    }
  }

  // Create a survey part/section — POST /survey/part/:id where :id is formId
  // (survey id), application/json, auth as query params. Body: { name, description, form_id, category_id, sub_category_id, sub_sub_category_id }
  async createSurveyPart(formId, partData = {}, extraParams = {}) {
    try {
      const isOptionsObj = typeof formId === "object" && formId !== null;
      const opts = isOptionsObj ? formId : extraParams;
      const rawFormId = isOptionsObj
        ? (formId.form_id || formId.formId || formId.survey_id || formId.surveyId || formId.id)
        : (formId || extraParams.form_id || extraParams.formId || extraParams.survey_id || extraParams.surveyId || extraParams.client_id || extraParams.clientId);

      const targetId = rawFormId ? (isNaN(rawFormId) ? rawFormId : Number(rawFormId)) : undefined;

      const body = {
        name: partData.name,
        description: partData.description,
        form_id: targetId,
        category_id: opts?.category_id ?? opts?.categoryId,
        sub_category_id: opts?.sub_category_id ?? opts?.subCategoryId,
        sub_sub_category_id: opts?.sub_sub_category_id ?? opts?.subSubCategoryId,
      };

      Object.keys(body).forEach((key) => {
        if (body[key] === undefined || body[key] === null) delete body[key];
      });

      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        form_id: targetId,
        category_id: opts?.category_id ?? opts?.categoryId,
        sub_category_id: opts?.sub_category_id ?? opts?.subCategoryId,
        sub_sub_category_id: opts?.sub_sub_category_id ?? opts?.subSubCategoryId,
        ...opts,
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === null) delete params[key];
      });
      const response = await axios.post(SURVEY_API_ENDPOINT.part(targetId || ""), body, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error creating survey part:", error);
      throw error;
    }
  }

  // Fetch survey parts — GET /survey/part/:id where :id is form_id,
  // query params: form_id, category_id, sub_category_id, sub_sub_category_id, page, limit, sortBy, sortOrder
  async fetchSurveyParts(formId, extraParams = {}) {
    try {
      const isOptionsObj = typeof formId === "object" && formId !== null;
      const opts = isOptionsObj ? formId : extraParams;
      const rawFormId = isOptionsObj
        ? (formId.form_id || formId.formId || formId.survey_id || formId.surveyId || formId.id)
        : (formId || extraParams.form_id || extraParams.formId || extraParams.survey_id || extraParams.surveyId || extraParams.client_id || extraParams.clientId);

      const targetId = rawFormId ? (isNaN(rawFormId) ? rawFormId : Number(rawFormId)) : undefined;

      const catId = opts?.category_id ?? opts?.categoryId;
      const subCatId = opts?.sub_category_id ?? opts?.subCategoryId;
      const subSubCatId = opts?.sub_sub_category_id ?? opts?.subSubCategoryId;

      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        form_id: targetId,
        category_id: catId !== undefined && catId !== null ? (isNaN(catId) ? catId : Number(catId)) : undefined,
        sub_category_id: subCatId !== undefined && subCatId !== null ? (isNaN(subCatId) ? subCatId : Number(subCatId)) : undefined,
        sub_sub_category_id: subSubCatId !== undefined && subSubCatId !== null ? (isNaN(subSubCatId) ? subSubCatId : Number(subSubCatId)) : undefined,
        page: opts?.page ?? 1,
        limit: opts?.limit ?? 10,
        sortBy: opts?.sortBy ?? "sort_order",
        sortOrder: opts?.sortOrder ?? "ASC",
        ...opts,
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === null) delete params[key];
      });
      const response = await axios.get(SURVEY_API_ENDPOINT.part(targetId || ""), { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching survey parts:", error);
      throw error;
    }
  }

  // Create a section under a part — POST /survey/section (application/json),
  // auth as query params.
  // Body: { client_id, title, description, media_name, media_type, part, part_id, sort_order, category_id, sub_category_id }
  async createSection(clientId, partId, partName, sectionData = {}, extraParams = {}) {
    try {
      const catId = sectionData.category_id || sectionData.categoryId || extraParams.category_id || extraParams.categoryId;
      const subCatId = sectionData.sub_category_id || sectionData.subCategoryId || extraParams.sub_category_id || extraParams.subCategoryId;

      const body = {
        client_id: clientId ? (isNaN(clientId) ? clientId : Number(clientId)) : clientId,
        title: sectionData.title,
        description: sectionData.description,
        media_name: sectionData.mediaName,
        media_type: sectionData.mediaType,
        part: partName,
        part_id: partId ? (isNaN(partId) ? partId : Number(partId)) : partId,
        sort_order: sectionData.sortOrder,
      };

      if (catId !== undefined && catId !== null && catId !== "") {
        body.category_id = isNaN(catId) ? catId : Number(catId);
      }
      if (subCatId !== undefined && subCatId !== null && subCatId !== "") {
        body.sub_category_id = isNaN(subCatId) ? subCatId : Number(subCatId);
      }

      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...extraParams,
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === null) delete params[key];
      });
      const response = await axios.post(SURVEY_API_ENDPOINT.section, body, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error creating section:", error);
      throw error;
    }
  }

  // Fetch sections for a part — GET /survey/section, client_id and part_id as query params
  async fetchSections(clientId, partId, extraParams = {}) {
    try {
      const isOptionsObject = clientId !== null && typeof clientId === "object";
      const {
        clientId: optClientId,
        partId: optPartId,
        categoryId,
        subCategoryId,
        category_id,
        sub_category_id,
      } = isOptionsObject ? clientId : { clientId, partId };

      const catId = category_id || categoryId || extraParams.category_id || extraParams.categoryId;
      const subCatId = sub_category_id || subCategoryId || extraParams.sub_category_id || extraParams.subCategoryId;

      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        client_id: optClientId || clientId,
        part_id: optPartId || partId,
        category_id: catId,
        sub_category_id: subCatId,
        ...extraParams,
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === null) delete params[key];
      });
      const response = await axios.get(SURVEY_API_ENDPOINT.section, { params });
      let resultData = response?.data;
      const rawList = Array.isArray(resultData)
        ? resultData
        : Array.isArray(resultData?.data)
        ? resultData.data
        : Array.isArray(resultData?.sections)
        ? resultData.sections
        : [];

      /* if (!optPartId && !partId && rawList.length === 0 && (extraParams.form_id || extraParams.survey_id || optClientId || clientId)) {
        try {
          const fallbackParams = {
            user_id: this.userId,
            auth_key: this.springToken || this.token,
          };
          const formIdVal = extraParams.form_id || extraParams.survey_id;
          if (formIdVal) {
            fallbackParams.form_id = formIdVal;
            fallbackParams.survey_id = formIdVal;
          }
          const clientVal = optClientId || clientId;
          if (clientVal) {
            fallbackParams.client_id = clientVal;
          }
          const fallbackRes = await axios.get(SURVEY_API_ENDPOINT.section, { params: fallbackParams });
          const fbData = fallbackRes?.data;
          const fbList = Array.isArray(fbData)
            ? fbData
            : Array.isArray(fbData?.data)
            ? fbData.data
            : Array.isArray(fbData?.sections)
            ? fbData.sections
            : [];
          if (fbList.length > 0) {
            return fbData;
          }
        } catch (fbErr) {}
      } */

      return resultData;
    } catch (error) {
      console.error("Error fetching sections:", error);
      throw error;
    }
  }

  // Alias for fetchSections
  async fetchSurveySections(clientId, partId) {
    return this.fetchSections(clientId, partId);
  }

  // Update an existing section — PUT /survey/section/:id (application/json),
  // auth as query params.
  // Body: { title, description, media_name, media_type, part, part_id, sort_order, category_id, sub_category_id }
  async updateSection(sectionId, sectionData = {}, extraParams = {}) {
    try {
      const catId = sectionData.category_id || sectionData.categoryId || extraParams.category_id || extraParams.categoryId;
      const subCatId = sectionData.sub_category_id || sectionData.subCategoryId || extraParams.sub_category_id || extraParams.subCategoryId;

      const body = {
        title: sectionData.title,
        description: sectionData.description,
        media_name: sectionData.mediaName,
        media_type: sectionData.mediaType,
        part: sectionData.part,
        part_id: sectionData.partId ? (isNaN(sectionData.partId) ? sectionData.partId : Number(sectionData.partId)) : undefined,
        sort_order: sectionData.sortOrder,
      };

      if (catId !== undefined && catId !== null && catId !== "") {
        body.category_id = isNaN(catId) ? catId : Number(catId);
      }
      if (subCatId !== undefined && subCatId !== null && subCatId !== "") {
        body.sub_category_id = isNaN(subCatId) ? subCatId : Number(subCatId);
      }

      // Drop undefined fields so a partial update doesn't null out other columns
      Object.keys(body).forEach((key) => {
        if (body[key] === undefined) delete body[key];
      });
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...extraParams,
      };
      const response = await axios.put(
        `${SURVEY_API_ENDPOINT.section}/${sectionId}`,
        body,
        { params, headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating section:", error);
      throw error;
    }
  }

  // Duplicate a section — POST /survey/section/:id/duplicate (application/json),
  // auth as query params.
  // Body: { initials } — optional, max 50 chars, trimmed; new title becomes
  // "[Original Title] - {initials}", or "[Original Title] - Copy" if omitted/null.
  async duplicateSection(sectionId, initials) {
    try {
      const trimmed = typeof initials === "string" ? initials.trim().slice(0, 50) : initials;
      const body = {
        initials: trimmed === undefined ? null : trimmed,
      };
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.post(SURVEY_API_ENDPOINT.sectionDuplicate(sectionId), body, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error duplicating section:", error);
      throw error;
    }
  }

  // Delete a section — DELETE /survey/section/:id
  async deleteSection(sectionId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.delete(
        `${SURVEY_API_ENDPOINT.section}/${sectionId}`,
        { params }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting section:", error);
      throw error;
    }
  }

  // Question endpoints
  // Fetch questions — GET /survey/question with query params
  // Query: { user_id, auth_key, client_id, part_id, section_id, form_id, search, limit, page, category_id, sub_category_id }
  async fetchQuestions(clientIdOrOptions, partId, extraOptions = {}) {
    try {
      const isOptionsObject =
        clientIdOrOptions !== null && typeof clientIdOrOptions === "object";

      const extra = typeof extraOptions === "object" ? extraOptions : {};

      const {
        clientId,
        partId: optPartId,
        sectionId,
        formId,
        search,
        limit,
        page,
        categoryId,
        subCategoryId,
        category_id,
        sub_category_id,
      } = isOptionsObject
        ? clientIdOrOptions
        : { clientId: clientIdOrOptions, partId, ...extra };

      const token = this.springToken || this.token;

      const catId = category_id || categoryId || extra.category_id || extra.categoryId;
      const subCatId = sub_category_id || subCategoryId || extra.sub_category_id || extra.subCategoryId;

      const params = {
        auth_key: token,
        user_id: this.userId,
        client_id: clientId ? (isNaN(clientId) ? clientId : Number(clientId)) : clientId,
        part_id: optPartId ? (isNaN(optPartId) ? optPartId : Number(optPartId)) : optPartId,
        section_id: sectionId,
        form_id: formId,
        category_id: catId ? (isNaN(catId) ? catId : Number(catId)) : undefined,
        sub_category_id: subCatId ? (isNaN(subCatId) ? subCatId : Number(subCatId)) : undefined,
        search,
        limit,
        page,
      };
      // Drop undefined so optional filters aren't sent unless provided
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === null) delete params[key];
      });

      const headers = {};
      if (token) {
        headers["Authorization"] = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      }

      const response = await axios.get(SURVEY_API_ENDPOINT.question, { params, headers });
      let resultData = response?.data;

      const rawList = Array.isArray(resultData)
        ? resultData
        : Array.isArray(resultData?.data)
        ? resultData.data
        : Array.isArray(resultData?.questions)
        ? resultData.questions
        : [];

      const isNotDeleted = (q) => !(q.is_deleted === 1 || q.is_deleted === true || q.is_deleted === "1" || q.isDeleted || q.deleted);

      /* if (rawList.length === 0 && (optPartId || formId)) {
        try {
          const fallbackParams = { auth_key: token, user_id: this.userId };
          if (optPartId) fallbackParams.part_id = optPartId;
          if (formId) fallbackParams.form_id = formId;
          if (catId) fallbackParams.category_id = catId;
          if (subCatId) fallbackParams.sub_category_id = subCatId;

          const fallbackRes = await axios.get(SURVEY_API_ENDPOINT.question, { params: fallbackParams, headers });
          const fbData = fallbackRes?.data;
          const fbList = Array.isArray(fbData)
            ? fbData
            : Array.isArray(fbData?.data)
            ? fbData.data
            : Array.isArray(fbData?.questions)
            ? fbData.questions
            : [];

          if (fbList.length > 0) {
            return fbData;
          }
        } catch (fbErr) {}
      } */

      const filteredRaw = rawList.filter(isNotDeleted);
      return Array.isArray(resultData) ? filteredRaw : { ...resultData, data: filteredRaw, questions: filteredRaw };
    } catch (error) {
      console.error("Error fetching questions:", error);
      throw error;
    }
  }

  // Alias for fetchQuestions
  async fetchSurveyQuestions(clientIdOrOptions, partId) {
    return this.fetchQuestions(clientIdOrOptions, partId);
  }


  // Create a question — POST /survey/question (application/json)
  async createQuestion(clientId, partId, partName, questionData = {}, extraParams = {}) {
    try {
      const token = this.springToken || this.token;
      const catId = questionData.category_id || questionData.categoryId || extraParams.category_id;
      const subCatId = questionData.sub_category_id || questionData.subCategoryId || extraParams.sub_category_id;

      const body = {
        client_id: clientId ? (isNaN(clientId) ? clientId : Number(clientId)) : clientId,
        part_id: partId ? (isNaN(partId) ? partId : Number(partId)) : partId,
        question_text: questionData.questionText || questionData.question_text || questionData.text,
        description: questionData.description !== undefined ? questionData.description : "",
        scale: questionData.scale ? (isNaN(questionData.scale) ? questionData.scale : Number(questionData.scale)) : 5,
        is_mandatory: questionData.isMandatory ? 1 : 0,
      };

      if (questionData.sectionId) {
        body.section_id = questionData.sectionId;
      }
      if (catId !== undefined && catId !== null) {
        body.category_id = isNaN(catId) ? catId : Number(catId);
      }
      if (subCatId !== undefined && subCatId !== null) {
        body.sub_category_id = isNaN(subCatId) ? subCatId : Number(subCatId);
      }
      if (questionData.mediaUrl) body.media_url = questionData.mediaUrl;
      if (questionData.mediaName) body.media_name = questionData.mediaName;
      if (questionData.mediaType) body.media_type = questionData.mediaType;
      if (partName) body.part = partName;
      if (questionData.sortOrder !== undefined) body.sort_order = questionData.sortOrder;

      // Drop undefined fields
      Object.keys(body).forEach((key) => {
        if (body[key] === undefined) delete body[key];
      });

      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      }

      const params = {
        user_id: this.userId,
        auth_key: token,
        ...extraParams,
      };
      if (catId !== undefined && catId !== null) params.category_id = catId;
      if (subCatId !== undefined && subCatId !== null) params.sub_category_id = subCatId;

      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === null) delete params[key];
      });

      const response = await axios.post(SURVEY_API_ENDPOINT.question, body, {
        params,
        headers,
      });
      return response.data;
    } catch (error) {
      console.error("Error creating question:", error);
      throw error;
    }
  }

  // Update an existing question — PUT /survey/question/:id (application/json),
  // auth as query params. Same body shape as createQuestion, partial update.
  // auth_key, user_id, client_id, and part_id are sent both as query
  // parameters and duplicated in the JSON body per the API spec.
  async updateQuestion(questionId, questionData = {}) {
    try {
      const authKey = this.springToken || this.token;
      const body = {
        auth_key: authKey,
        user_id: this.userId,
        client_id: questionData.clientId,
        part_id: questionData.partId,
        section_id: questionData.sectionId,
        question_text: questionData.questionText,
        description: questionData.description,
        scale: questionData.scale,
        is_mandatory:
          questionData.isMandatory === undefined ? undefined : questionData.isMandatory ? 1 : 0,
        media_url: questionData.mediaUrl,
        media_name: questionData.mediaName,
        media_type: questionData.mediaType,
        part: questionData.part,
        sort_order: questionData.sortOrder,
      };
      Object.keys(body).forEach((key) => {
        if (body[key] === undefined) delete body[key];
      });
      const params = {
        user_id: this.userId,
        auth_key: authKey,
      };
      const response = await axios.put(SURVEY_API_ENDPOINT.questionDetail(questionId), body, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error updating question:", error);
      throw error;
    }
  }

  // Delete a question — DELETE /survey/question/:id
  async deleteQuestion(questionId, extraParams = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...extraParams,
      };
      let response;
      try {
        response = await axios.delete(SURVEY_API_ENDPOINT.questionDetail(questionId), { params });
      } catch (err1) {
        response = await axios.delete(`${SURVEY_API_ENDPOINT.question}/${questionId}`, { params });
      }
      return response ? response.data : { success: true };
    } catch (error) {
      console.error("Error deleting question:", error);
      throw error;
    }
  }

  // Upload an attachment on a section or question — POST /survey/attachment
  // (multipart/form-data), auth as query params. Same pattern as uploadLogo:
  // send the raw file straight through, the backend does the S3 upload and
  // creates the attachment record in one shot.
  // attachableType must be "section" or "question".
  async uploadAttachment(attachableType, attachableId, file) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("attachable_type", attachableType);
      formData.append("attachable_id", attachableId);

      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };

      const response = await axios.post(SURVEY_API_ENDPOINT.attachment, formData, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      console.error("Error uploading attachment:", error);
      throw error;
    }
  }

  // Fetch survey history for a client — GET /survey/history or falls back
  async fetchSurveyHistory(clientId, params = {}) {
    try {
      const requestParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        client_id: clientId,
        ...params,
      };
      const response = await axios.get(`${SURVEY_API_ENDPOINT.listNode}/history`, {
        params: requestParams,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching survey history:", error);
      throw error;
    }
  }

  // Bulk-upload a survey's questions/sections from a file — POST /survey/:id/upload
  // Path param `:id` prioritizes form_id / survey_id (e.g. 28 or 30), fallback to client_id.
  // Form-data fields: file, client_id, form_id, survey_id, part_id, userId, user_id, auth_key.
  async uploadSurvey(clientIdOrId, file, formIdOrOptions = null, partId = null, extraParams = {}) {
    try {
      let clientId = clientIdOrId;
      let formId = typeof formIdOrOptions === "object" && formIdOrOptions !== null ? formIdOrOptions.formId || formIdOrOptions.form_id : formIdOrOptions;
      let surveyId = typeof formIdOrOptions === "object" && formIdOrOptions !== null ? formIdOrOptions.surveyId || formIdOrOptions.survey_id : extraParams.surveyId || extraParams.survey_id;
      let targetPartId = typeof formIdOrOptions === "object" && formIdOrOptions !== null ? formIdOrOptions.partId || formIdOrOptions.part_id : partId;
      let uploadFile = file;

      if (typeof clientIdOrId === "object" && clientIdOrId !== null && !(clientIdOrId instanceof File) && !(clientIdOrId instanceof Blob)) {
        clientId = clientIdOrId.clientId || clientIdOrId.client_id;
        uploadFile = clientIdOrId.file || file;
        formId = clientIdOrId.formId || clientIdOrId.form_id || formId;
        surveyId = clientIdOrId.surveyId || clientIdOrId.survey_id || surveyId;
        targetPartId = clientIdOrId.partId || clientIdOrId.part_id || targetPartId;
      }

      // Prioritize formId or surveyId for path param :id (e.g., 28 or 30), fallback to clientId
      const pathParamId = formId || surveyId || clientId;

      const formData = new FormData();
      if (uploadFile) {
        formData.append("file", uploadFile);
      }

      const resolvedClientId = clientId || extraParams.client_id || extraParams.clientId;
      if (resolvedClientId != null) {
        formData.append("client_id", resolvedClientId);
      }

      const resolvedFormId = formId || surveyId;
      if (resolvedFormId != null) {
        formData.append("form_id", resolvedFormId);
      }

      const resolvedSurveyId = surveyId || formId;
      if (resolvedSurveyId != null) {
        formData.append("survey_id", resolvedSurveyId);
      }

      if (targetPartId != null) {
        formData.append("part_id", targetPartId);
      }

      // The survey's configured scale (3 or 5) - scale is no longer read
      // from the Excel file itself, so it must be sent explicitly here for
      // the backend to know which scale to apply to every imported question.
      const resolvedScale = extraParams?.scale ?? extraParams?.scale_option ?? extraParams?.scaleOption;
      if (resolvedScale != null && resolvedScale !== "") {
        formData.append("scale", resolvedScale);
      }

      if (this.userId != null) {
        formData.append("user_id", this.userId);
        formData.append("userId", this.userId);
      }

      if (this.springToken || this.token) {
        formData.append("auth_key", this.springToken || this.token);
      }

      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };

      const response = await axios.post(SURVEY_API_ENDPOINT.upload(pathParamId), formData, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      console.error("Error uploading survey file:", error);
      throw error;
    }
  }

  // Alias method for uploadSectionsAndQuestions matching uploadSurvey
  async uploadSectionsAndQuestions(clientIdOrId, file, formIdOrOptions = null, partId = null, extraParams = {}) {
    return this.uploadSurvey(clientIdOrId, file, formIdOrOptions, partId, extraParams);
  }

  // Fetch the "Created Survey" listing (year-wise survey table on the
  // summary page) — GET /survey/created, scoped to a client via client_id.
  // Supports pagination/filter params (page, per_page, status, etc).
  async fetchCreatedSurveys(clientId, params = {}) {
    try {
      const requestParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        client_id: clientId,
        ...params,
      };
      const response = await axios.get(SURVEY_API_ENDPOINT.created, { params: requestParams });
      return response.data;
    } catch (error) {
      console.error("Error fetching created surveys:", error);
      throw error;
    }
  }

  // Fetch survey listing for the Survey Summary table — GET /survey/form-list,
  // scoped to a client via client_id. Supports pagination/filter params
  // (page, per_page, status, etc).
  async fetchSurveyFormList(clientId, params = {}) {
    try {
      const requestParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...(clientId && clientId !== "all" ? { client_id: clientId } : {}),
        ...params,
      };
      const response = await axios.get(SURVEY_API_ENDPOINT.formList, { params: requestParams });
      return response.data;
    } catch (error) {
      console.error("Error fetching survey form list:", error);
      throw error;
    }
  }

  // Fetch a single survey's form details — GET /api/v1/survey/:survey_id?category_id=69&sub_category_id=70
  async fetchSurveyForm(surveyId, extraParams = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...extraParams,
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === null) delete params[key];
      });

      let response;
      try {
        response = await axios.get(SURVEY_API_ENDPOINT.surveyDetailsById(surveyId), { params });
      } catch (e1) {
        try {
          response = await axios.get(SURVEY_API_ENDPOINT.form(surveyId), { params });
        } catch (e2) {
          response = await axios.get(SURVEY_API_ENDPOINT.surveyForm(surveyId), { params });
        }
      }
      return response ? response.data : { success: false, data: {} };
    } catch (error) {
      console.error("Error fetching survey form details:", error);
      return { success: false, data: {} };
    }
  }

  // Build the JSON body for POST /survey/save-form — the full survey tree
  // (parts -> sections -> questions) plus an `action` field that tells the
  // backend whether to persist this as "draft" or "publish". Same shape is
  // used for both actions; only `action` differs.
  // surveyId should be passed once a survey already exists (i.e. this is an
  // update, not the very first save) — without it the backend has no way to
  // tell "update this survey" apart from "create a new one" for a client
  // that already has other surveys.
  buildSurveyFormPayload(clientId, action, { surveyId, name, description, parts = [], questions = [] } = {}) {
    const resolvedSurveyId =
      surveyId ||
      (clientId ? localStorage.getItem(`active_survey_id_${clientId}`) : null);

    const body = {
      survey_id: resolvedSurveyId !== undefined && resolvedSurveyId !== null && String(resolvedSurveyId) !== String(clientId) ? (Number(resolvedSurveyId) || resolvedSurveyId) : undefined,
      client_id: clientId ? (Number(clientId) || clientId) : clientId,
      action,
      status: action === "draft" ? "DRAFT" : "PUBLISHED",
      survey_status: action === "draft" ? "DRAFT" : "PUBLISHED",
      is_published: action === "draft" ? 0 : 1,
      // `name` here is the Questionnaire's own name, not the parent Survey's
      // name — those are separate entities. Previously this was also sent as
      // `survey_name`, which the backend appears to treat as the authoritative
      // name for the Survey record itself, so editing the questionnaire's
      // name was silently overwriting the survey's name too. Only send the
      // questionnaire-scoped field here.
      questionnaire_name: name,
      name,
      description,
      parts: parts.map((part, partIdx) => ({
        id: part.id,
        name: part.name,
        sort_order: part.sortOrder ?? partIdx + 1,
        sections: (part.sections || []).map((section, sectionIdx) => ({
          id: section.id,
          title: section.title,
          sort_order: section.sortOrder ?? sectionIdx + 1,
          questions: (section.questions || []).map((question, questionIdx) => ({
            id: question.id,
            question_text: question.questionText || question.question_text || question.text,
            scale: question.scale || 5,
            is_mandatory: question.isMandatory || question.is_mandatory ? 1 : 0,
            sort_order: question.sortOrder ?? questionIdx + 1,
          })),
        })),
      })),
      questions: (questions || []).map((question, questionIdx) => ({
        id: question.id,
        question_text: question.questionText || question.question_text || question.text,
        scale: question.scale || 5,
        is_mandatory: question.isMandatory || question.is_mandatory ? 1 : 0,
        sort_order: question.sortOrder ?? questionIdx + 1,
      })),
    };

    Object.keys(body).forEach((key) => {
      if (body[key] === undefined) delete body[key];
    });

    return body;
  }

  // Save a survey — POST /survey/save-form (application/json), auth as query
  // params. Handles both "Save as Draft" and "Publish": pass action = "draft"
  // or "publish" and the backend persists the corresponding status.
  async saveSurveyForm(clientId, action, surveyData = {}) {
    try {
      const body = this.buildSurveyFormPayload(clientId, action, surveyData);
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.post(SURVEY_API_ENDPOINT.saveForm, body, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error(`Error ${action === "publish" ? "publishing" : "saving"} survey:`, error);
      throw error;
    }
  }

  // Download the sample survey upload template — GET /survey/sample-download.
  // Returns the raw file as a blob (plus response headers) so the caller can
  // trigger a browser download, same pattern as exportClient.
  async downloadSampleFile() {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };

      const response = await axios.get(SURVEY_API_ENDPOINT.sampleDownload, {
        params,
        responseType: "blob",
      });
      return response;
    } catch (error) {
      console.error("Error downloading sample file:", error);
      throw error;
    }
  }

  // Fetch share details for a survey — GET /api/v1/survey/:id/share
  async fetchShareDetails(surveyId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };

      let response;
      try {
        response = await axios.get(SURVEY_API_ENDPOINT.surveyShare(surveyId), { params });
      } catch (e) {
        response = await axios.get(SURVEY_API_ENDPOINT.shareDetails(surveyId), { params });
      }
      return response.data;
    } catch (error) {
      console.error("Error fetching survey share details:", error);
      throw error;
    }
  }

  // Fetch QR code for a survey — GET /api/v1/survey/:id/qr
  async fetchPublicQrCode(surveyId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.get(SURVEY_API_ENDPOINT.publicQrCode(surveyId), { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching public QR code:", error);
      throw error;
    }
  }

  // GET /api/survey/:id/multiple-qr: Dedicated endpoint returning JSON payload of all generated QR code Data URLs and public share links
  async fetchMultipleQr(surveyId, params = {}) {
    try {
      const queryParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...params,
      };
      const response = await axios.get(SURVEY_API_ENDPOINT.multipleQr(surveyId), { params: queryParams });
      return response.data;
    } catch (error) {
      console.error("Error fetching multiple QR codes:", error);
      throw error;
    }
  }

  // GET /api/survey/:id/download-all-qr: Export endpoint returning structured JSON payload with filenames, metadata, and Base64 PNG QR code strings
  async fetchDownloadAllQr(surveyId, params = {}) {
    try {
      const queryParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...params,
      };
      const response = await axios.get(SURVEY_API_ENDPOINT.downloadAllQr(surveyId), { params: queryParams });
      return response.data;
    } catch (error) {
      console.error("Error fetching download all QR codes:", error);
      throw error;
    }
  }

  // GET /api/public/survey/:id/qr: Accepts category_id, sub_category_id, sub_sub_category_id, and form_id in query parameters to render binary PNG images
  async fetchPublicSurveyQr(surveyId, params = {}) {
    try {
      const response = await axios.get(SURVEY_API_ENDPOINT.publicSurveyQr(surveyId), {
        params,
        responseType: "arraybuffer",
      });
      return response;
    } catch (error) {
      console.error("Error rendering binary public survey QR code image:", error);
      throw error;
    }
  }

  // Submit responses to a public survey — POST /survey/:id/response
  async submitPublicSurveyResponse(surveyId, payload) {
    try {
      const response = await axios.post(
        SURVEY_API_ENDPOINT.publicResponse(surveyId),
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error submitting public survey response:", error);
      throw error;
    }
  }




  // Fetch scoring ranges for a category
async fetchScoringCategory(categoryId) {
  try {
    const params = {
      user_id: this.userId,
      auth_key: this.springToken || this.token,
    };

    const response = await axios.get(
      SURVEY_API_ENDPOINT.scoringCategory(categoryId),
      { params }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching scoring category:", error);
    throw error;
  }
}

// Create a scoring range for a category
async createScoringCategory(categoryId, scoringData) {
  try {
    const params = {
      user_id: this.userId,
      auth_key: this.springToken || this.token,
    };

    const response = await axios.post(
      SURVEY_API_ENDPOINT.scoringCategory(categoryId),
      scoringData,
      {
        params,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error creating scoring category:", error);
    throw error;
  }
}


// Update an existing scoring range
async updateScoringCategory(scoringId, scoringData) {
  try {
    const params = {
      user_id: this.userId,
      auth_key: this.springToken || this.token,
    };

    const response = await axios.put(
      SURVEY_API_ENDPOINT.scoringCategoryUpdate(scoringId),
      scoringData,
      {
        params,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error updating scoring category:", error);
    throw error;
  }
}


// Delete an existing scoring range
async deleteScoringCategory(scoringId) {
  try {
    const params = {
      user_id: this.userId,
      auth_key: this.springToken || this.token,
    };

    const response = await axios.delete(
      SURVEY_API_ENDPOINT.scoringCategoryDelete(scoringId),
      {
        params,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error deleting scoring category:", error);
    throw error;
  }
}

// Upload scoring Excel file
async uploadScoringFile(file) {
  try {
    const formData = new FormData();

    formData.append("file", file);
    formData.append("userId", this.userId);

    const params = {
      user_id: this.userId,
      auth_key: this.springToken || this.token,
    };

    const response = await axios.post(
      SURVEY_API_ENDPOINT.scoringUpload,
      formData,
      {
        params,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error uploading scoring file:", error);
    throw error;
  }
}

// Download scoring Excel sample
async downloadScoringSample() {
  try {
    const params = {
      user_id: this.userId,
      auth_key: this.springToken || this.token,
    };

    const response = await axios.get(
      SURVEY_API_ENDPOINT.scoringSampleDownload,
      {
        params,
        responseType: "blob",
      }
    );

    return response;
  } catch (error) {
    console.error("Error downloading scoring sample:", error);
    throw error;
  }
}

  // Fetch response conversion statistics for Top Management Analysis — GET /survey-stats/response-conversion/:id
  async fetchResponseConversion(id, params = {}) {
    try {
      const requestParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...params,
      };

      const response = await axios.get(
        SURVEY_API_ENDPOINT.responseConversion(id),
        { params: requestParams }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching response conversion statistics:", error);
      throw error;
    }
  }
  // GET /survey/:survey_id — Get by Survey ID
  async getBySurveyId(surveyId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.get(SURVEY_API_ENDPOINT.getBySurveyId(surveyId), { params });
      return response.data;
    } catch (error) {
      console.error("Error calling getBySurveyId:", error);
      throw error;
    }
  }

  // GET /client/:client_id — Get by Client ID (Creation Mode)
  async getByClientId(clientId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.get(SURVEY_API_ENDPOINT.getByClientId(clientId), { params });
      return response.data;
    } catch (error) {
      console.error("Error calling getByClientId:", error);
      throw error;
    }
  }

  // Fetch survey basic details — GET /survey-basic-details/survey/:survey_id or /survey-basic-details/:id
  async fetchSurveyBasicDetails(id) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        survey_id: id,
      };

      let response;
      try {
        response = await axios.get(SURVEY_API_ENDPOINT.basicDetailsBySurvey(id), { params });
      } catch (e) {
        response = await axios.get(SURVEY_API_ENDPOINT.basicDetails(id), { params });
      }
      return response.data;
    } catch (error) {
      console.error("Error fetching survey basic details:", error);
      throw error;
    }
  }

  // Save survey basic details — POST /api/v1/survey/basic-details or /api/v1/survey/basic-details/survey/:survey_id
  async saveSurveyBasicDetails(id, basicDetailsData = {}) {
    try {
      const clientIdVal = basicDetailsData?.client_id || basicDetailsData?.clientId;
      const surveyIdVal = basicDetailsData?.survey_id || id;
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      if (surveyIdVal) params.survey_id = surveyIdVal;
      if (clientIdVal) params.client_id = clientIdVal;

      const bodyPayload = {
        ...(surveyIdVal ? { survey_id: Number(surveyIdVal) || surveyIdVal } : {}),
        ...(clientIdVal ? { client_id: Number(clientIdVal) || clientIdVal } : {}),
        ...basicDetailsData,
      };

      let response;
      try {
        response = await axios.post(SURVEY_API_ENDPOINT.basicDetails(surveyIdVal), bodyPayload, {
          params,
          headers: { "Content-Type": "application/json" },
        });
      } catch (err1) {
        try {
          response = await axios.post(SURVEY_API_ENDPOINT.basicDetailsBySurvey(surveyIdVal), bodyPayload, {
            params,
            headers: { "Content-Type": "application/json" },
          });
        } catch (err2) {
          response = await axios.post(SURVEY_API_ENDPOINT.basicDetailsAlt(surveyIdVal), bodyPayload, {
            params,
            headers: { "Content-Type": "application/json" },
          });
        }
      }
      return response.data;
    } catch (error) {
      console.error("Error saving survey basic details:", error);
      throw error;
    }
  }

  // ==========================================
  // SURVEY SETTINGS APIS
  // ==========================================

  // 1. Page Settings (Load configuration sections)
  // Scenario 1: New Survey Creation (under Client ID) -> GET /api/v1/survey/settings/client/:clientId?is_new=true
  // Scenario 2: Editing Survey #surveyId -> GET /api/v1/survey/settings/survey/:surveyId
  async fetchFullPageSettings(targetId, isNew = false) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      if (isNew) {
        params.is_new = "true";
      } else {
        params.survey_id = targetId;
      }
      let response;
      if (isNew) {
        try {
          response = await axios.get(SURVEY_API_ENDPOINT.loadFullPageSettingsClient(targetId), { params });
        } catch (e1) {
          try {
            response = await axios.get(SURVEY_API_ENDPOINT.loadFullPageSettingsPageClient(targetId), { params });
          } catch (e2) {
            response = await axios.get(SURVEY_API_ENDPOINT.loadFullPageSettingsPage(targetId), { params });
          }
        }
      } else {
        try {
          response = await axios.get(SURVEY_API_ENDPOINT.loadFullPageSettingsSurvey(targetId), { params });
        } catch (e1) {
          try {
            response = await axios.get(SURVEY_API_ENDPOINT.loadFullPageSettingsPageSurvey(targetId), { params });
          } catch (e2) {
            try {
              response = await axios.get(SURVEY_API_ENDPOINT.loadFullPageSettings(targetId), { params });
            } catch (e3) {
              response = await axios.get(SURVEY_API_ENDPOINT.getBySurveyId(targetId), { params });
            }
          }
        }
      }
      return response ? response.data : { success: true, data: {} };
    } catch (error) {
      console.error("Error fetching full survey page settings:", error);
      return { success: false, data: {} };
    }
  }

  // Bulk save settings — POST /api/v1/survey/settings/page/save-all or /api/v1/survey/settings/page/:survey_id/save-all
  async saveAllPageSettings(surveyId, payload = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      let response;
      try {
        response = await axios.post(SURVEY_API_ENDPOINT.saveAllPageSettingsPageSurvey(surveyId), payload, {
          params,
          headers: { "Content-Type": "application/json" },
        });
      } catch (e1) {
        try {
          response = await axios.post(SURVEY_API_ENDPOINT.saveAllPageSettingsPage, payload, {
            params,
            headers: { "Content-Type": "application/json" },
          });
        } catch (e2) {
          try {
            response = await axios.post(SURVEY_API_ENDPOINT.saveAllPageSettings(surveyId), payload, {
              params,
              headers: { "Content-Type": "application/json" },
            });
          } catch (e3) {
            response = await axios.post(SURVEY_API_ENDPOINT.saveAllPageSettingsAlt(surveyId), payload, {
              params,
              headers: { "Content-Type": "application/json" },
            });
          }
        }
      }
      return response.data;
    } catch (error) {
      console.error("Error saving all survey page settings:", error);
      throw error;
    }
  }

  // 2. Plant Section APIs
  // GET /api/v1/survey-settings/plant?survey_id=1&search=&page=1&limit=10
  async fetchPlants(params = {}) {
    try {
      const requestParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...params,
      };
      const response = await axios.get(SURVEY_API_ENDPOINT.surveyPlant, { params: requestParams });
      return response.data;
    } catch (error) {
      console.error("Error fetching plants:", error);
      throw error;
    }
  }

  // POST /api/v1/survey-settings/plant
  async addPlant(plantData = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.post(SURVEY_API_ENDPOINT.surveyPlant, plantData, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error adding plant:", error);
      throw error;
    }
  }

  // PUT /api/v1/survey-settings/plant/:id
  async updatePlant(id, plantData = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.put(SURVEY_API_ENDPOINT.surveyPlantDetail(id), plantData, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error updating plant:", error);
      throw error;
    }
  }

  // DELETE /api/v1/survey-settings/plant/:id
  async deletePlant(id) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.delete(SURVEY_API_ENDPOINT.surveyPlantDetail(id), { params });
      return response.data;
    } catch (error) {
      console.error("Error deleting plant:", error);
      throw error;
    }
  }

  // 3. Gender Section APIs
  // GET /api/v1/survey-settings/gender?survey_id=1
  async fetchGenderSettings(surveyId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        survey_id: surveyId,
      };
      const response = await axios.get(SURVEY_API_ENDPOINT.surveyGender, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching gender settings:", error);
      throw error;
    }
  }

  // POST /api/v1/survey-settings/gender
  async saveGenderSettings(genderData = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.post(SURVEY_API_ENDPOINT.surveyGender, genderData, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error saving gender settings:", error);
      throw error;
    }
  }

  // 4. Age Scale & Age-Range Builder APIs
  // GET /api/v1/survey-settings/age-scale?survey_id=1
  async fetchAgeScale(surveyId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        survey_id: surveyId,
      };
      const response = await axios.get(SURVEY_API_ENDPOINT.surveyAgeScale, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching age scale settings:", error);
      throw error;
    }
  }

  // POST /api/v1/survey-settings/age-scale
  async saveAgeScale(ageData = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.post(SURVEY_API_ENDPOINT.surveyAgeScale, ageData, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error saving age scale settings:", error);
      throw error;
    }
  }

  // DELETE /api/v1/survey-settings/age-range/:id
  async deleteAgeRange(id) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.delete(SURVEY_API_ENDPOINT.surveyAgeRangeDetail(id), { params });
      return response.data;
    } catch (error) {
      console.error("Error deleting age range:", error);
      throw error;
    }
  }

  // 5. Designation Section APIs
  // GET /designation or /survey-settings/designation?survey_id=1&search=&page=1&limit=10
  async fetchDesignations(params = {}) {
    try {
      const requestParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...params,
      };
      try {
        const response = await axios.get(SURVEY_API_ENDPOINT.surveyDesignation, { params: requestParams });
        return response.data;
      } catch (err) {
        if (SURVEY_API_ENDPOINT.surveyDesignationAlt) {
          const fallbackRes = await axios.get(SURVEY_API_ENDPOINT.surveyDesignationAlt, { params: requestParams });
          return fallbackRes.data;
        }
        throw err;
      }
    } catch (error) {
      console.error("Error fetching designations:", error);
      throw error;
    }
  }

  // GET by ID: GET /designation/:id
  async fetchDesignationById(id, params = {}) {
    try {
      const requestParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...params,
      };
      try {
        const response = await axios.get(SURVEY_API_ENDPOINT.surveyDesignationDetail(id), { params: requestParams });
        return response.data;
      } catch (err) {
        if (SURVEY_API_ENDPOINT.surveyDesignationDetailAlt) {
          const fallbackRes = await axios.get(SURVEY_API_ENDPOINT.surveyDesignationDetailAlt(id), { params: requestParams });
          return fallbackRes.data;
        }
        throw err;
      }
    } catch (error) {
      console.error("Error fetching designation by ID:", error);
      throw error;
    }
  }

  // POST: POST /designation or POST /designation/:id
  async addDesignation(data = {}, explicitId = null) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const targetId = explicitId || data?.id;
      const targetUrl = targetId
        ? SURVEY_API_ENDPOINT.surveyDesignationDetail(targetId)
        : SURVEY_API_ENDPOINT.surveyDesignation;
      try {
        const response = await axios.post(targetUrl, data, {
          params,
          headers: { "Content-Type": "application/json" },
        });
        return response.data;
      } catch (err) {
        const altUrl = targetId && SURVEY_API_ENDPOINT.surveyDesignationDetailAlt
          ? SURVEY_API_ENDPOINT.surveyDesignationDetailAlt(targetId)
          : SURVEY_API_ENDPOINT.surveyDesignationAlt;
        if (altUrl) {
          const fallbackRes = await axios.post(altUrl, data, {
            params,
            headers: { "Content-Type": "application/json" },
          });
          return fallbackRes.data;
        }
        throw err;
      }
    } catch (error) {
      console.error("Error adding designation:", error);
      throw error;
    }
  }

  // PUT: PUT /designation/:id
  async updateDesignation(id, data = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      try {
        const response = await axios.put(SURVEY_API_ENDPOINT.surveyDesignationDetail(id), data, {
          params,
          headers: { "Content-Type": "application/json" },
        });
        return response.data;
      } catch (err) {
        if (SURVEY_API_ENDPOINT.surveyDesignationDetailAlt) {
          const fallbackRes = await axios.put(SURVEY_API_ENDPOINT.surveyDesignationDetailAlt(id), data, {
            params,
            headers: { "Content-Type": "application/json" },
          });
          return fallbackRes.data;
        }
        throw err;
      }
    } catch (error) {
      console.error("Error updating designation:", error);
      throw error;
    }
  }

  // DELETE /designation/:id
  async deleteDesignation(id) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.delete(SURVEY_API_ENDPOINT.surveyDesignationDetail(id), { params });
      return response.data;
    } catch (error) {
      console.error("Error deleting designation:", error);
      throw error;
    }
  }

  // 6. Department Section APIs
  // GET /department or /survey-settings/department?survey_id=1&search=&page=1&limit=10
  async fetchDepartments(params = {}) {
    try {
      const requestParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...params,
      };
      try {
        const response = await axios.get(SURVEY_API_ENDPOINT.surveyDepartment, { params: requestParams });
        return response.data;
      } catch (err) {
        if (SURVEY_API_ENDPOINT.surveyDepartmentAlt) {
          const fallbackRes = await axios.get(SURVEY_API_ENDPOINT.surveyDepartmentAlt, { params: requestParams });
          return fallbackRes.data;
        }
        throw err;
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  }

  // GET by ID: GET /department/:id
  async fetchDepartmentById(id, params = {}) {
    try {
      const requestParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...params,
      };
      try {
        const response = await axios.get(SURVEY_API_ENDPOINT.surveyDepartmentDetail(id), { params: requestParams });
        return response.data;
      } catch (err) {
        if (SURVEY_API_ENDPOINT.surveyDepartmentDetailAlt) {
          const fallbackRes = await axios.get(SURVEY_API_ENDPOINT.surveyDepartmentDetailAlt(id), { params: requestParams });
          return fallbackRes.data;
        }
        throw err;
      }
    } catch (error) {
      console.error("Error fetching department by ID:", error);
      throw error;
    }
  }

  // POST: POST /department or POST /department/:id
  async addDepartment(data = {}, explicitId = null) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const targetId = explicitId || data?.id;
      const targetUrl = targetId
        ? SURVEY_API_ENDPOINT.surveyDepartmentDetail(targetId)
        : SURVEY_API_ENDPOINT.surveyDepartment;
      try {
        const response = await axios.post(targetUrl, data, {
          params,
          headers: { "Content-Type": "application/json" },
        });
        return response.data;
      } catch (err) {
        const altUrl = targetId && SURVEY_API_ENDPOINT.surveyDepartmentDetailAlt
          ? SURVEY_API_ENDPOINT.surveyDepartmentDetailAlt(targetId)
          : SURVEY_API_ENDPOINT.surveyDepartmentAlt;
        if (altUrl) {
          const fallbackRes = await axios.post(altUrl, data, {
            params,
            headers: { "Content-Type": "application/json" },
          });
          return fallbackRes.data;
        }
        throw err;
      }
    } catch (error) {
      console.error("Error adding department:", error);
      throw error;
    }
  }

  // PUT: PUT /department/:id
  async updateDepartment(id, data = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      try {
        const response = await axios.put(SURVEY_API_ENDPOINT.surveyDepartmentDetail(id), data, {
          params,
          headers: { "Content-Type": "application/json" },
        });
        return response.data;
      } catch (err) {
        if (SURVEY_API_ENDPOINT.surveyDepartmentDetailAlt) {
          const fallbackRes = await axios.put(SURVEY_API_ENDPOINT.surveyDepartmentDetailAlt(id), data, {
            params,
            headers: { "Content-Type": "application/json" },
          });
          return fallbackRes.data;
        }
        throw err;
      }
    } catch (error) {
      console.error("Error updating department:", error);
      throw error;
    }
  }

  // DELETE /department/:id
  async deleteDepartment(id) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.delete(SURVEY_API_ENDPOINT.surveyDepartmentDetail(id), { params });
      return response.data;
    } catch (error) {
      console.error("Error deleting department:", error);
      throw error;
    }
  }

  // 7. Education Section APIs
  // GET /education or /survey-settings/education?survey_id=1&search=&page=1&limit=10
  async fetchEducations(params = {}) {
    try {
      const requestParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...params,
      };
      try {
        const response = await axios.get(SURVEY_API_ENDPOINT.surveyEducation, { params: requestParams });
        return response.data;
      } catch (err) {
        if (SURVEY_API_ENDPOINT.surveyEducationAlt) {
          const fallbackRes = await axios.get(SURVEY_API_ENDPOINT.surveyEducationAlt, { params: requestParams });
          return fallbackRes.data;
        }
        throw err;
      }
    } catch (error) {
      console.error("Error fetching educations:", error);
      throw error;
    }
  }

  // GET by ID: GET /education/:id
  async fetchEducationById(id, params = {}) {
    try {
      const requestParams = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...params,
      };
      try {
        const response = await axios.get(SURVEY_API_ENDPOINT.surveyEducationDetail(id), { params: requestParams });
        return response.data;
      } catch (err) {
        if (SURVEY_API_ENDPOINT.surveyEducationDetailAlt) {
          const fallbackRes = await axios.get(SURVEY_API_ENDPOINT.surveyEducationDetailAlt(id), { params: requestParams });
          return fallbackRes.data;
        }
        throw err;
      }
    } catch (error) {
      console.error("Error fetching education by ID:", error);
      throw error;
    }
  }

  // POST: POST /education or POST /education/:id
  async addEducation(data = {}, explicitId = null) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const targetId = explicitId || data?.id || data?.education_id;
      const targetUrl = targetId
        ? SURVEY_API_ENDPOINT.surveyEducationDetail(targetId)
        : SURVEY_API_ENDPOINT.surveyEducation;

      try {
        const response = await axios.post(targetUrl, data, {
          params,
          headers: { "Content-Type": "application/json" },
        });
        return response.data;
      } catch (err) {
        const altUrl = targetId && SURVEY_API_ENDPOINT.surveyEducationDetailAlt
          ? SURVEY_API_ENDPOINT.surveyEducationDetailAlt(targetId)
          : SURVEY_API_ENDPOINT.surveyEducationAlt;
        if (altUrl) {
          const fallbackRes = await axios.post(altUrl, data, {
            params,
            headers: { "Content-Type": "application/json" },
          });
          return fallbackRes.data;
        }
        throw err;
      }
    } catch (error) {
      console.error("Error adding education:", error);
      throw error;
    }
  }

  // PUT /education/:id
  async updateEducation(id, data = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.put(SURVEY_API_ENDPOINT.surveyEducationDetail(id), data, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error updating education:", error);
      throw error;
    }
  }

  // DELETE /education/:id
  async deleteEducation(id) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.delete(SURVEY_API_ENDPOINT.surveyEducationDetail(id), { params });
      return response.data;
    } catch (error) {
      console.error("Error deleting education:", error);
      throw error;
    }
  }

  // ==========================================
  // MANAGEMENT STRUCTURE APIS
  // ==========================================

  // GET /api/v1/survey/settings/management/tree/:survey_id (or /api/v1/management-structure/tree/:survey_id)
  async fetchManagementStructureTree(surveyId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      let response;
      try {
        response = await axios.get(SURVEY_API_ENDPOINT.managementTreeAlt(surveyId), { params });
      } catch (e) {
        response = await axios.get(SURVEY_API_ENDPOINT.managementStructureTree(surveyId), { params });
      }
      return response.data;
    } catch (error) {
      console.error("Error fetching management structure tree:", error);
      throw error;
    }
  }

  // GET /categories/:parent_id/sub-categories or /management-structure/categories/:parent_id/sub-categories
  async fetchManagementSubCategories(parentId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      try {
        const response = await axios.get(SURVEY_API_ENDPOINT.categoriesSubCategories(parentId), { params });
        return response.data;
      } catch (err1) {
        const response = await axios.get(SURVEY_API_ENDPOINT.managementSubCategories(parentId), { params });
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching sub-categories:", error);
      throw error;
    }
  }

  // GET /api/v1/management-structure/categories
  async fetchManagementCategories(surveyId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      if (surveyId) params.survey_id = surveyId;
      const response = await axios.get(SURVEY_API_ENDPOINT.managementCategories, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching management categories:", error);
      throw error;
    }
  }

  // POST /api/v1/management-structure/categories (Single Category Node)
  async createCategory(categoryData = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.post(SURVEY_API_ENDPOINT.managementCategories, categoryData, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error creating category node:", error);
      throw error;
    }
  }

  // POST /categories/:parent_id/sub-categories (Bulk/Single Save Sub-Categories)
  async bulkSaveSubCategories(parentId, payload = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      try {
        const response = await axios.post(SURVEY_API_ENDPOINT.categoriesSubCategories(parentId), payload, {
          params,
          headers: { "Content-Type": "application/json" },
        });
        return response.data;
      } catch (err1) {
        const response = await axios.post(SURVEY_API_ENDPOINT.managementSubCategories(parentId), payload, {
          params,
          headers: { "Content-Type": "application/json" },
        });
        return response.data;
      }
    } catch (error) {
      console.error("Error bulk saving sub-categories:", error);
      throw error;
    }
  }

  // PUT /api/v1/survey/settings/management/categories/:id
  async updateManagementCategory(id, categoryData = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const bodyPayload = {
        name: categoryData.name,
        description: categoryData.description,
        code: categoryData.code || categoryData.category_code,
        sort_order: categoryData.sort_order || categoryData.sortOrder || 1,
        is_active: categoryData.is_active !== undefined ? categoryData.is_active : true,
      };
      Object.keys(bodyPayload).forEach((k) => bodyPayload[k] === undefined && delete bodyPayload[k]);

      let response;
      try {
        response = await axios.put(SURVEY_API_ENDPOINT.managementCategoryDetail(id), bodyPayload, {
          params,
          headers: { "Content-Type": "application/json" },
        });
      } catch (err1) {
        try {
          response = await axios.put(SURVEY_API_ENDPOINT.managementCategoryDetailAlt1(id), bodyPayload, {
            params,
            headers: { "Content-Type": "application/json" },
          });
        } catch (err2) {
          try {
            response = await axios.put(SURVEY_API_ENDPOINT.managementCategoryDetailAlt2(id), bodyPayload, {
              params,
              headers: { "Content-Type": "application/json" },
            });
          } catch (err3) {
            try {
              response = await axios.put(SURVEY_API_ENDPOINT.managementSubCategoryDetailAlt1(id), bodyPayload, {
                params,
                headers: { "Content-Type": "application/json" },
              });
            } catch (err4) {
              response = await axios.put(SURVEY_API_ENDPOINT.managementSubCategoryDetailAlt2(id), bodyPayload, {
                params,
                headers: { "Content-Type": "application/json" },
              });
            }
          }
        }
      }
      return response.data;
    } catch (error) {
      console.error("Error updating management category/sub-category:", error);
      throw error;
    }
  }

  // DELETE /api/v1/survey/settings/management/categories/:id
  async deleteManagementCategory(id) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      let response;
      try {
        response = await axios.delete(SURVEY_API_ENDPOINT.managementCategoryDetail(id), { params });
      } catch (err1) {
        try {
          response = await axios.delete(SURVEY_API_ENDPOINT.managementCategoryDetailAlt1(id), { params });
        } catch (err2) {
          try {
            response = await axios.delete(SURVEY_API_ENDPOINT.managementCategoryDetailAlt2(id), { params });
          } catch (err3) {
            try {
              response = await axios.delete(SURVEY_API_ENDPOINT.managementSubCategoryDetailAlt1(id), { params });
            } catch (err4) {
              response = await axios.delete(SURVEY_API_ENDPOINT.managementSubCategoryDetailAlt2(id), { params });
            }
          }
        }
      }
      return response.data;
    } catch (error) {
      console.error("Error deleting management category/sub-category:", error);
      throw error;
    }
  }

  // GET /survey/settings/management/structure/:survey_id or /management-structure/structure-type/:survey_id
  async fetchManagementStructureType(surveyId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      let response;
      try {
        response = await axios.get(SURVEY_API_ENDPOINT.structureMode(surveyId), { params });
      } catch (e) {
        response = await axios.get(SURVEY_API_ENDPOINT.managementStructureType(surveyId), { params });
      }
      return response.data;
    } catch (error) {
      console.error("Error fetching management structure type:", error);
      throw error;
    }
  }

  // POST /api/v1/survey/settings/management/structure (or /api/v1/survey/settings/management/structure-type)
  async saveManagementStructureType(payload = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      let response;
      try {
        response = await axios.post(SURVEY_API_ENDPOINT.saveStructureMode, payload, {
          params,
          headers: { "Content-Type": "application/json" },
        });
      } catch (e1) {
        try {
          response = await axios.post(SURVEY_API_ENDPOINT.saveStructureType, payload, {
            params,
            headers: { "Content-Type": "application/json" },
          });
        } catch (e2) {
          response = await axios.post(SURVEY_API_ENDPOINT.saveManagementStructureType, payload, {
            params,
            headers: { "Content-Type": "application/json" },
          });
        }
      }
      return response.data;
    } catch (error) {
      console.error("Error saving management structure type:", error);
      throw error;
    }
  }

  // POST /api/v1/survey/settings/management/config
  async saveManagementStructureConfig(payload = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      let response;
      try {
        response = await axios.post(SURVEY_API_ENDPOINT.saveStructureConfig, payload, {
          params,
          headers: { "Content-Type": "application/json" },
        });
      } catch (e) {
        response = await axios.post(SURVEY_API_ENDPOINT.saveStructureMode, payload, {
          params,
          headers: { "Content-Type": "application/json" },
        });
      }
      return response.data;
    } catch (error) {
      console.error("Error saving management structure config tree:", error);
      throw error;
    }
  }
  // GET /survey-settings/management/structure-page/:survey_id
  async fetchManagementStructurePage(surveyId, extraParams = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
        ...extraParams,
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === null) delete params[key];
      });

      let response;
      try {
        response = await axios.get(SURVEY_API_ENDPOINT.managementStructurePage(surveyId), { params });
      } catch (e1) {
        try {
          response = await axios.get(`${Url.nodeClientURL}/survey/settings/management/structure-page/${surveyId}`, { params });
        } catch (e2) {
          response = await axios.get(`${Url.nodeClientURL}/management-structure/structure-page/${surveyId}`, { params });
        }
      }
      return response ? response.data : { success: false, data: {} };
    } catch (error) {
      console.error("Error fetching management structure page details:", error);
      return { success: false, data: {} };
    }
  }

  // GET /api/v1/survey/question/scale-options?scale=3 or 5
  async fetchQuestionScaleOptions(scale) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.get(SURVEY_API_ENDPOINT.surveyQuestionScaleOptions(scale), { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching question scale options:", error);
      throw error;
    }
  }

  // POST /api/v1/survey/question/scale-options
  async saveQuestionScaleOptions(payload = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.post(SURVEY_API_ENDPOINT.saveSurveyQuestionScaleOptions, payload, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error saving question scale options:", error);
      throw error;
    }
  }

  // GET /api/v1/scoring/category/:categoryId
  async fetchCategoryScoringScale(categoryId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.get(SURVEY_API_ENDPOINT.categoryScoringScale(categoryId), { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching category scoring scale:", error);
      throw error;
    }
  }

  // GET /api/v1/survey/:survey_id?category_id=69&sub_category_id=70
  async fetchSurveyFormAlt(surveyId, extraParams = {}) {
    return this.fetchSurveyForm(surveyId, extraParams);
  }

  // GET /api/v1/survey/:survey_id/share
  async fetchSurveyShare(surveyId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const response = await axios.get(SURVEY_API_ENDPOINT.surveyShare(surveyId), { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching survey share links:", error);
      return { success: false, data: {} };
    }
  }

  // GET /sub-categories/:parent_id/sub-sub-categories or /survey-settings/management/sub-sub-categories
  async fetchSubSubCategories(subCategoryId) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      if (subCategoryId) params.sub_category_id = subCategoryId;
      if (subCategoryId) {
        try {
          const response = await axios.get(SURVEY_API_ENDPOINT.subCategoriesSubSubCategories(subCategoryId), { params });
          return response.data;
        } catch (err1) {
          // fallback to managementSubSubCategories below
        }
      }
      const response = await axios.get(SURVEY_API_ENDPOINT.managementSubSubCategories, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching sub-sub-categories:", error);
      throw error;
    }
  }

  // POST /sub-categories/:parent_id/sub-sub-categories or /survey-settings/management/sub-sub-categories
  async saveSubSubCategory(payload = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const bodyData = {
        name: payload.name ? payload.name.trim() : "",
        description: payload.description || "",
        sort_order: payload.sort_order || 1,
        ...payload,
      };
      const subCatId = payload.sub_category_id || payload.subCategoryId || payload.sub_category || payload.parentId;
      if (subCatId) {
        try {
          const response = await axios.post(SURVEY_API_ENDPOINT.subCategoriesSubSubCategories(subCatId), bodyData, {
            params,
            headers: { "Content-Type": "application/json" },
          });
          return response.data;
        } catch (err1) {
          // fallback to managementSubSubCategories below
        }
      }
      const response = await axios.post(SURVEY_API_ENDPOINT.managementSubSubCategories, bodyData, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error saving sub-sub-category:", error);
      throw error;
    }
  }

  // PUT /api/v1/survey-settings/management/sub-sub-categories/:id
  async updateSubSubCategory(id, payload = {}) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const url = id ? `${SURVEY_API_ENDPOINT.managementSubSubCategories}/${id}` : SURVEY_API_ENDPOINT.managementSubSubCategories;
      const response = await axios.put(url, payload, {
        params,
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error("Error updating sub-sub-category:", error);
      throw error;
    }
  }

  // DELETE /api/v1/survey-settings/management/sub-sub-categories/:id
  async deleteSubSubCategory(id) {
    try {
      const params = {
        user_id: this.userId,
        auth_key: this.springToken || this.token,
      };
      const url = `${SURVEY_API_ENDPOINT.managementSubSubCategories}/${id}`;
      const response = await axios.delete(url, { params });
      return response.data;
    } catch (error) {
      console.error("Error deleting sub-sub-category:", error);
      throw error;
    }
  }
}

export default new SurveyApiService();